import { CopyIcon } from 'lucide-react'
import { Fragment } from 'react'

import { DocumentQuote } from '@/gen/artifacts/v1/model_pb'

import { mergeOverlappingContexts } from '@/lib/assessment/quote-matching'

import { Button } from '@/components/lemmala-design-system/button/button'
import { Hyperlink } from '@/components/lemmala-design-system/link/hyperlink'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'
import { useToast } from '@/components/lemmala-design-system/toast/use-toast'
import { QuoteWithContext } from '@/components/shared/source-document-static-viewer/quote-with-context'

type SourceDocumentStaticViewerProps = {
  documentQuote: DocumentQuote
  link?: string
}

export const SourceDocumentStaticViewer = ({
  documentQuote,
  link,
}: SourceDocumentStaticViewerProps) => {
  const { name, page } = documentQuote
  // Full-document citations reference the document only, with no verbatim text.
  // Drop empty entries so the card renders as a bare citation (name + page).
  const entries = documentQuote.quoteEntries.filter((e) => e.text.trim() !== '')
  const quotes = entries.map((e) => e.text)
  const quoteContexts = entries.map((e) => e.context)
  const { toast } = useToast()

  const handleCopyQuotes = () => {
    navigator.clipboard.writeText(quotes.join('\n'))
    toast({
      title: 'Quotes copied to clipboard',
      variant: 'success',
    })
  }

  return (
    <div className='rounded border border-main-border-default p-4'>
      <div className='flex items-center justify-between gap-2'>
        {link ? (
          <Hyperlink href={link} className='font-medium'>
            {name}
          </Hyperlink>
        ) : (
          <div className='font-medium'>{name}</div>
        )}
        <div className='flex items-center gap-2'>
          {!!page && <p className='text-main-foreground-tertiary text-body-small'>Page {page}</p>}
          {quotes.length > 0 && (
            <Button onClick={handleCopyQuotes} variant='text' size='sm' className='print:hidden'>
              <TextWithIcon icon={<CopyIcon size={16} />} text='Copy quotes' />
            </Button>
          )}
        </div>
      </div>
      <div className='mt-2 flex flex-col gap-2'>
        {quoteContexts && quoteContexts.length > 0
          ? mergeOverlappingContexts(quotes, quoteContexts).map((chunk, index) =>
              chunk.mergedContext ? (
                <QuoteWithContext key={index} context={chunk.mergedContext} quotes={chunk.quotes} />
              ) : (
                <Fragment key={index}>
                  {chunk.quotes.map((q, qi) => (
                    <div
                      key={`${index}-${qi}`}
                      className='text-main-foreground-primary text-body-default'
                    >
                      ...{q}...
                    </div>
                  ))}
                </Fragment>
              ),
            )
          : quotes.map((quote, index) => (
              <div key={index} className='text-main-foreground-primary text-body-default'>
                ...{quote}...
              </div>
            ))}
      </div>
    </div>
  )
}
